import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Handshake, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionTitle } from "@/components/SectionTitle";
import { IMAGES } from "@/lib/images";
import { useSeo } from "@/lib/useSeo";
import { listPartners } from "@/lib/db";

function TierPlaceholder({ title, subtitle }) {
  return (
    <div
      className="col-span-full flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-gold/30 bg-royal/20 px-8 py-12 text-center"
      data-testid="partner-tier-placeholder"
    >
      <div className="grid h-14 w-14 place-items-center rounded-full bg-gold/10 ring-1 ring-gold/30">
        <Handshake size={26} className="text-gold" />
      </div>
      <h4 className="font-heading text-xl font-800 tracking-tight text-white">{title}</h4>
      <p className="max-w-md text-sm leading-relaxed text-ice/65">{subtitle}</p>
      <Link
        to="/contact"
        data-testid="partner-tier-cta"
        className="mt-1 inline-flex items-center gap-2 rounded-full border-2 border-gold px-6 py-2.5 font-display text-sm font-700 uppercase tracking-wide text-gold transition-all duration-200 hover:bg-gold hover:text-navy"
      >
        Γίνε Συνεργάτης <ArrowRight size={16} />
      </Link>
    </div>
  );
}

export default function Partners() {
  const [partners, setPartners] = useState([]);
  useSeo({ title: "Συνεργάτες", description: "Οι συνεργάτες και υποστηρικτές της Ρεάλ Δυτικής Αττικής. Γίνε κι εσύ συνεργάτης και στήριξε τον τοπικό αθλητισμό." });

  useEffect(() => {
    listPartners().then((d) => setPartners(d)).catch(() => {});
  }, []);

  const gold = partners.filter((p) => p.tier === "gold");
  const silver = partners.filter((p) => p.tier === "silver");
  const supporters = partners.filter((p) => p.tier === "supporter");

  return (
    <div>
      {/* BANNER */}
      <section className="relative flex h-[42vh] min-h-[320px] items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.partnersBanner})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-navy/40" />
        <div className="club-container relative z-10 pb-12">
          <p className="font-display text-sm font-600 uppercase tracking-[0.3em] text-gold">Μαζί δυνατοί</p>
          <h1 className="font-heading mt-2 text-5xl font-900 uppercase tracking-tight text-white sm:text-7xl">
            Οι Συνεργάτες μας
          </h1>
        </div>
      </section>

      <section className="club-container py-16 sm:py-24">
        <Reveal className="mx-auto max-w-3xl text-center">
          <p className="text-lg leading-relaxed text-ice/85">
            Οι συνεργάτες και οι υποστηρικτές μας αποτελούν αναπόσπαστο κομμάτι της οικογένειας της
            Ρεάλ Δυτικής Αττικής. Χάρη στην πολύτιμη στήριξή τους μπορούμε να αναπτύσσουμε τον τοπικό
            αθλητισμό, να επενδύουμε στις υποδομές μας και να δίνουμε ευκαιρίες σε νέους αθλητές.
          </p>
        </Reveal>

        {/* GOLD */}
        <div className="mt-20">
          <SectionTitle kicker="Κορυφαία στήριξη" title="Χρυσοί Συνεργάτες" className="mb-10" />
          <div className="grid gap-7 md:grid-cols-2">
            {gold.length > 0 ? gold.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <div
                  className="group flex h-full flex-col gap-4 rounded-2xl border-2 border-gold/40 bg-gradient-to-br from-royal/50 to-navy p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-[0_20px_50px_-18px_rgba(179,144,74,0.7)]"
                  data-testid={`partner-gold-${p.id}`}
                >
                  <div className="flex h-24 items-center">
                    {p.logo ? (
                      <img src={p.logo} alt={p.name} className="max-h-20 object-contain" />
                    ) : (
                      <span className="font-heading text-3xl font-900 tracking-tight text-gold">
                        {p.name}
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading text-2xl font-800 text-white">{p.name}</h3>
                  <p className="flex-1 text-sm leading-relaxed text-ice/80">{p.description}</p>
                  {p.website && (
                    <a
                      href={p.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 font-display text-sm font-700 uppercase tracking-wide text-brand hover:text-gold"
                    >
                      Ιστοσελίδα <ExternalLink size={15} />
                    </a>
                  )}
                </div>
              </Reveal>
            )) : (
              <TierPlaceholder title="Γίνε ο πρώτος Χρυσός Συνεργάτης μας" subtitle="Η κορυφαία θέση προβολής είναι διαθέσιμη — μεγάλο λογότυπο, περιγραφή και σύνδεσμος προς την ιστοσελίδα σας. Συνδέστε το brand σας με έναν δυναμικό σύλλογο." />
            )}
          </div>
        </div>

        {/* SILVER */}
        <div className="mt-20">
          <SectionTitle kicker="Σταθεροί σύμμαχοι" title="Ασημένιοι Συνεργάτες" className="mb-10" />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {silver.length > 0 ? silver.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <div
                  className="group flex h-full flex-col gap-3 rounded-xl border border-white/15 bg-royal/40 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-brand hover:shadow-[0_16px_40px_-16px_rgba(60,115,192,0.65)]"
                  data-testid={`partner-silver-${p.id}`}
                >
                  <div className="flex h-16 items-center">
                    {p.logo ? (
                      <img src={p.logo} alt={p.name} className="max-h-14 object-contain" />
                    ) : (
                      <span className="font-heading text-2xl font-800 tracking-tight text-ice">
                        {p.name}
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading text-xl font-800 text-white">{p.name}</h3>
                  <p className="text-sm text-ice/75">{p.description}</p>
                </div>
              </Reveal>
            )) : (
              <TierPlaceholder title="Θέσεις Ασημένιων Συνεργατών διαθέσιμες" subtitle="Στηρίξτε τον σύλλογο και αποκτήστε προβολή προς το κοινό μας. Ελάτε να σχεδιάσουμε μαζί μια συνεργασία που ταιριάζει σε εσάς." />
            )}
          </div>
        </div>

        {/* SUPPORTERS */}
        <div className="mt-20">
          <SectionTitle kicker="Η οικογένειά μας" title="Υποστηρικτές" className="mb-10" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {supporters.length > 0 ? supporters.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.04}>
                <div
                  className="flex h-24 items-center justify-center rounded-xl border border-white/10 bg-navy/60 px-4 text-center transition-colors hover:border-gold/50"
                  data-testid={`partner-supporter-${p.id}`}
                >
                  {p.logo ? (
                    <img src={p.logo} alt={p.name} className="max-h-12 object-contain" />
                  ) : (
                    <span className="font-display text-lg font-700 uppercase tracking-wide text-ice">
                      {p.name}
                    </span>
                  )}
                </div>
              </Reveal>
            )) : (
              <TierPlaceholder title="Γίνε Υποστηρικτής του συλλόγου" subtitle="Κάθε στήριξη μετράει! Δείξτε την αγάπη σας για τον τοπικό αθλητισμό και γίνετε μέλος της μεγάλης μας οικογένειας." />
            )}
          </div>
        </div>

        {/* CTA */}
        <Reveal className="mt-24">
          <div className="relative overflow-hidden rounded-3xl border border-gold/30 bg-gradient-to-r from-royal to-navy p-10 text-center sm:p-16">
            <Handshake className="mx-auto text-gold" size={44} />
            <h3 className="font-heading mt-5 text-4xl font-900 uppercase tracking-tight text-white sm:text-5xl">
              Γίνε Συνεργάτης
            </h3>
            <p className="mx-auto mt-4 max-w-xl text-ice/85">
              Συνδέστε το brand σας με έναν δυναμικό σύλλογο και στηρίξτε τον τοπικό αθλητισμό.
              Επικοινωνήστε μαζί μας για να σχεδιάσουμε μαζί μια συνεργασία που ταιριάζει σε εσάς.
            </p>
            <Link
              to="/contact"
              data-testid="partners-cta-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-display text-lg font-700 uppercase tracking-wide text-navy transition-all duration-200 hover:bg-gold/85 hover:shadow-[0_0_30px_rgba(179,144,74,0.6)]"
            >
              Επικοινωνήστε μαζί μας <ArrowRight size={20} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
